import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Camera, Loader2, Trash2, Info } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API_BASE, cleanerApi, getCleanerToken, unwrap } from "@/lib/api";
import { compressImage } from "@/lib/imageCompression";

const PHASES = ["before", "after"];
const CLOSED_STATUSES = new Set(["completed", "cancelled", "refunded", "coordination_cancelled"]);

export default function WorkPhotos({ bookingId, bookingStatus, initialPhotos = [], L }) {
  const [photos, setPhotos] = useState(initialPhotos);
  const [uploading, setUploading] = useState(false);
  const [pendingPhase, setPendingPhase] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingLabel, setPendingLabel] = useState("");
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);

  const closed = CLOSED_STATUSES.has(bookingStatus);

  const reload = useCallback(async () => {
    try {
      const r = await cleanerApi.get(`/cleaner/bookings/${bookingId}/work-photos`);
      setPhotos(unwrap(r) || []);
    } catch { /* keep current */ }
  }, [bookingId]);

  useEffect(() => { reload(); }, [reload]);

  const grouped = useMemo(() => {
    const g = { before: [], after: [] };
    (photos || []).forEach((p) => { if (g[p.phase]) g[p.phase].push(p); });
    return g;
  }, [photos]);

  const onPick = (phase) => {
    if (closed) return;
    setPendingPhase(phase);
    (phase === "before" ? beforeInputRef : afterInputRef).current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setPendingFile(file);
  };

  const doUpload = async () => {
    if (!pendingFile || !pendingPhase) return;
    setUploading(true);
    try {
      const compressed = await compressImage(pendingFile, { maxSize: 1600, quality: 0.8 });
      const fd = new FormData();
      fd.append("file", compressed, compressed.name || "photo.jpg");
      fd.append("phase", pendingPhase);
      if (pendingLabel.trim()) fd.append("room_label", pendingLabel.trim());
      const r = await cleanerApi.post(`/cleaner/bookings/${bookingId}/work-photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((prev) => [...prev, unwrap(r)]);
      toast.success(L("Photo uploaded", "Foto subida"));
      setPendingFile(null);
      setPendingLabel("");
      setPendingPhase(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || L("Upload failed", "No pudimos subir la foto"));
    } finally {
      setUploading(false);
    }
  };

  const cancelPending = () => { setPendingFile(null); setPendingLabel(""); setPendingPhase(null); };

  const remove = async (photo) => {
    if (closed) return;
    if (!window.confirm(L("Delete this photo?", "¿Borrar esta foto?"))) return;
    try {
      await cleanerApi.delete(`/cleaner/bookings/${bookingId}/work-photos/${photo.id}`);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || L("Could not delete", "No pudimos borrar"));
    }
  };

  return (
    <Card className="rounded-2xl p-6 border border-[#E5E7EB]" data-testid="cleaner-work-photos">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-display font-bold flex items-center gap-2">
          <Camera className="w-4 h-4 text-[#0EA5A4]" /> {L("Work photos", "Fotos del trabajo")}
        </h3>
      </div>
      <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 text-xs mb-4 flex items-start gap-2" data-testid="work-photos-privacy-notice">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>{L(
          "Do not photograph personal documents, faces, or valuables. Focus on the cleaned areas.",
          "No fotografíes documentos personales, rostros ni objetos de valor. Enfoca las áreas limpiadas.",
        )}</span>
      </div>

      <input
        ref={beforeInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFileChosen}
        data-testid="work-photos-input-before"
      />
      <input
        ref={afterInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFileChosen}
        data-testid="work-photos-input-after"
      />

      {PHASES.map((phase) => (
        <div key={phase} className="mb-5 last:mb-0">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-[#0C1A1A] capitalize">
              {phase === "before" ? L("Before photos", "Fotos de antes") : L("After photos", "Fotos de después")}
              <span className="ml-2 text-xs text-[#9CA3AF] font-normal">({grouped[phase].length})</span>
            </h4>
            <Button
              onClick={() => onPick(phase)}
              disabled={closed || uploading}
              size="sm"
              className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white"
              data-testid={`add-${phase}-btn`}
            >
              <Camera className="w-3.5 h-3.5 mr-1.5" />
              {phase === "before" ? L("Add before photo", "Agregar foto de antes") : L("Add after photo", "Agregar foto de después")}
            </Button>
          </div>
          {grouped[phase].length === 0 ? (
            <div className="text-xs text-[#9CA3AF] italic">{L("No photos yet", "Todavía no hay fotos")}</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid={`${phase}-grid`}>
              {grouped[phase].map((p) => (
                <Thumbnail key={p.id} photo={p} bookingId={bookingId} onDelete={remove} disabled={closed} L={L} />
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Label prompt after picking a file */}
      {pendingFile && (
        <div className="mt-4 rounded-xl bg-[#F5F5F4] border border-[#E5E7EB] p-3" data-testid="pending-upload-panel">
          <div className="text-sm font-semibold mb-2">
            {L("Label this photo (optional)", "Etiqueta esta foto (opcional)")}
          </div>
          <Input
            value={pendingLabel}
            onChange={(e) => setPendingLabel(e.target.value)}
            placeholder={L("e.g. Kitchen, Bathroom…", "ej. Cocina, Baño…")}
            className="rounded-xl mb-3"
            maxLength={80}
            data-testid="pending-room-label"
          />
          <div className="flex gap-2">
            <Button variant="outline" onClick={cancelPending} disabled={uploading} className="rounded-xl flex-1" data-testid="cancel-upload-btn">
              {L("Cancel", "Cancelar")}
            </Button>
            <Button onClick={doUpload} disabled={uploading} className="rounded-xl flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Upload", "Subir")}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Thumbnail({ photo, bookingId, onDelete, disabled, L }) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let active = true;
    let blobUrl = "";
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/cleaner/bookings/${bookingId}/work-photos/${photo.id}`, {
          headers: { Authorization: `Bearer ${getCleanerToken()}` },
        });
        if (!r.ok) return;
        const blob = await r.blob();
        blobUrl = URL.createObjectURL(blob);
        if (active) setUrl(blobUrl);
      } catch { /* keep empty */ }
    })();
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [bookingId, photo.id]);

  return (
    <div className="relative group" data-testid="work-photo-thumb">
      <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F4] border border-[#E5E7EB] grid place-items-center">
        {url ? (
          <img src={url} alt={photo.room_label || "work photo"} className="w-full h-full object-cover" />
        ) : (
          <Loader2 className="w-4 h-4 animate-spin text-[#9CA3AF]" />
        )}
      </div>
      {photo.room_label && (
        <div className="text-[10px] font-mono text-[#4B5563] mt-1 truncate">{photo.room_label}</div>
      )}
      {!disabled && (
        <button
          onClick={() => onDelete(photo)}
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label={L("Delete photo", "Borrar foto")}
          data-testid="delete-work-photo-btn"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
